import { Spacing } from '@persona-id/design-tokens';
import { pick } from 'lodash/fp';
import qs from 'qs';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import ModalWrapper from 'dashboard/components/ModalWrapper';
import { RoutedTabbedSection } from 'dashboard/components/TabbedSectionV2';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import FormCurrentUserEmail, {
  FormCurrentUserEmailData,
} from 'dashboard/forms/FormCurrentUserEmail';
import FormCurrentUserPassword, {
  FormCurrentUserPasswordData,
} from 'dashboard/forms/FormCurrentUserPassword';
import FormCurrentUserPersonalDetails from 'dashboard/forms/FormCurrentUserPersonalDetails';
import { FormCurrentUserPersonalDetailsData } from 'dashboard/forms/FormCurrentUserPersonalDetails/FormCurrentUserPersonalDetails';
import FormCurrentUserProfilePicture, {
  FormCurrentUserProfilePictureData,
} from 'dashboard/forms/FormCurrentUserProfilePicture';
import { Container, Header, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import { Organization } from 'dashboard/models/organization';
import { MfaMethod, User } from 'dashboard/models/user';
import MultiFactorAuthentication from 'dashboard/views/DashboardUserProfileView/MultiFactorAuthentication';
import UserNotificationSettings, {
  UserNotificationSettingsUpdateData,
} from 'dashboard/views/DashboardUserProfileView/UserNotificationSettings';
import Card from 'design-system/components/Card';
import Input from 'design-system/components/Input';
import useConfirmation from 'lib/hooks/useConfirmation';
import { FeatureFlagsMap } from 'lib/interfaces/feature-flags';

interface DashboardUserProfileViewProps {
  currentUser: User;
  organization: Organization;
  startUpdateEmailAddress: (values: FormCurrentUserEmailData) => Promise<void>;
  finishUpdateEmailAddress: (code: string) => Promise<void>;
  updatePersonalDetails: (values: FormCurrentUserPersonalDetailsData) => Promise<void>;
  updatePassword: (values: FormCurrentUserPasswordData) => Promise<void>;
  updateProfilePicture: (values: FormCurrentUserProfilePictureData) => Promise<void>;
  setupMfaEmail: () => Promise<void>;
  enableMfaEmail: (token: string) => Promise<void>;
  disableMfaEmail: () => Promise<void>;
  mfaEmailLoading: boolean;
  setupMfaPhone: () => Promise<void>;
  enableMfaPhone: (code: string) => Promise<void>;
  disableMfaPhone: () => Promise<void>;
  mfaPhoneLoading: boolean;
  setupMfaTotp: () => Promise<void>;
  enableMfaTotp: (code: string) => Promise<void>;
  disableMfaTotp: () => Promise<void>;
  mfaTotpLoading: boolean;
  recoveryCode: string | undefined;
  rotateRecoveryCode: () => Promise<void>;
  rotateRecoveryCodeLoading: boolean;
  featureFlags: FeatureFlagsMap;
  updateUserNotificationSettings: (values: UserNotificationSettingsUpdateData) => Promise<void>;
}

const Wrapper = styled('div')`
  display: flex;
`;

const SideWrapper = styled('div')`
  flex: 1 1 0;
  flex-direction: column;
`;

const ProfilePictureCardWrapper = styled('div')`
  margin-right: ${Spacing[4]};
`;

const DashboardUserProfileView = ({
  currentUser,
  organization,
  startUpdateEmailAddress,
  finishUpdateEmailAddress,
  updatePersonalDetails,
  updatePassword,
  updateProfilePicture,
  setupMfaEmail,
  enableMfaEmail,
  disableMfaEmail,
  mfaEmailLoading,
  setupMfaPhone,
  enableMfaPhone,
  disableMfaPhone,
  mfaPhoneLoading,
  setupMfaTotp,
  enableMfaTotp,
  disableMfaTotp,
  mfaTotpLoading,
  recoveryCode,
  rotateRecoveryCode,
  rotateRecoveryCodeLoading,
  updateUserNotificationSettings,
}: DashboardUserProfileViewProps) => {
  const { 'email-address': emailAddress, 'enabled-mfa-methods': enabledMfaMethods } =
    currentUser.attributes;
  const enabledMfaMethodsSet = new Set(enabledMfaMethods);

  const { 'has-saml-enabled': hasSamlEnabled } = organization.attributes;

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const { confirmation_token: confirmationToken } = qs.parse(location.search, {
      ignoreQueryPrefix: true,
    });

    if (confirmationToken != null && typeof confirmationToken === 'string') {
      navigate(location.pathname);
      finishUpdateEmailAddress(confirmationToken);
    }
  }, [finishUpdateEmailAddress]);

  const [showReAuthModal, { prompt, confirm, cancel }] = useConfirmation();

  const handleSaveEmailAddressChange = async (formValues: FormCurrentUserEmailData) => {
    const shouldReAuthEmail =
      enabledMfaMethodsSet.has(MfaMethod.Email) &&
      currentUser.attributes['email-address'] !== formValues['emailAddress'];
    if (shouldReAuthEmail) {
      await prompt(async () => {
        // note(ashley): this triggers a confirmation email now, so we dont need to disable/reenable mfa anymore
        await startUpdateEmailAddress(formValues);
      });
    } else {
      await startUpdateEmailAddress(formValues);
    }
  };

  const handleSavePersonalDetailChanges = async (
    formValues: FormCurrentUserPersonalDetailsData,
  ) => {
    const shouldReAuthPhone =
      enabledMfaMethodsSet.has(MfaMethod.Phone) &&
      currentUser.attributes['phone-number'] !== formValues['phone-number'];
    if (shouldReAuthPhone) {
      await prompt(async () => {
        await disableMfaPhone();
        await updatePersonalDetails(formValues);
        await setupMfaPhone();
      });
    } else {
      await updatePersonalDetails(formValues);
    }
  };

  const handleSaveProfilePictureChange = async (
    profilePicture: FormCurrentUserProfilePictureData,
  ) => {
    await updateProfilePicture(profilePicture);
  };

  if (hasSamlEnabled) {
    return (
      <Container>
        <Header data-test='user__header' title='Profile' />
        <Section data-test='dashboard-account-profile-view__saml-enabled'>
          <RoutedTabbedSection
            tabs={[
              {
                name: 'Personal details',
                path: '',
                contents: (
                  <>
                    <Card>
                      <Card.Body>
                        <Card.Title>Personal details</Card.Title>
                        <FormCurrentUserPersonalDetails
                          currentUser={currentUser}
                          initialValues={pick(
                            ['name-first', 'name-last', 'phone-number', 'timezone'],
                            currentUser.attributes,
                          )}
                          onSubmit={handleSavePersonalDetailChanges}
                          samlEnabled
                        />
                      </Card.Body>
                    </Card>
                    <Card>
                      <Card.Body>
                        <Card.Title>Email address</Card.Title>
                        <Input disabled={true} value={emailAddress} />
                      </Card.Body>
                    </Card>
                  </>
                ),
                dataTest: 'user-profile-view__tab--general',
              },
              {
                name: 'Notifications',
                path: '/notifications',
                contents: (
                  <UserNotificationSettings
                    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                    userNotificationPolicy={currentUser.attributes['notification-policy']}
                    updateUserNotificationSettings={updateUserNotificationSettings}
                  />
                ),
                dataTest: 'user-profile-view__tab--notifications',
              },
            ]}
          />
        </Section>
      </Container>
    );
  } else {
    return (
      <Container>
        <Header data-test='user__header' title='Profile' />
        <Section data-test='dashboard-account-profile-view__saml-disabled'>
          <RoutedTabbedSection
            tabs={[
              {
                name: 'Personal details',
                path: '',
                contents: (
                  <Wrapper>
                    <ProfilePictureCardWrapper>
                      <Card>
                        <Card.Body>
                          <FormCurrentUserProfilePicture
                            currentUser={currentUser}
                            onSubmit={handleSaveProfilePictureChange}
                          />
                        </Card.Body>
                      </Card>
                    </ProfilePictureCardWrapper>
                    <SideWrapper>
                      <Card>
                        <Card.Body>
                          <Card.Title>Personal details</Card.Title>
                          <FormCurrentUserPersonalDetails
                            currentUser={currentUser}
                            initialValues={pick(
                              ['name-first', 'name-last', 'phone-number', 'timezone'],
                              currentUser.attributes,
                            )}
                            onSubmit={handleSavePersonalDetailChanges}
                          />
                        </Card.Body>
                      </Card>
                      <Card>
                        <Card.Body>
                          <Card.Title>Change email address</Card.Title>
                          <FormCurrentUserEmail
                            initialValues={{ emailAddress: emailAddress }}
                            onSubmit={handleSaveEmailAddressChange}
                          />
                        </Card.Body>
                      </Card>
                    </SideWrapper>
                  </Wrapper>
                ),
                dataTest: 'user-profile-view__tab--general',
              },
              {
                name: 'Notifications',
                path: '/notifications',
                contents: (
                  <UserNotificationSettings
                    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                    userNotificationPolicy={currentUser.attributes['notification-policy']}
                    updateUserNotificationSettings={updateUserNotificationSettings}
                  />
                ),
                dataTest: 'user-profile-view__tab--notifications',
              },
              {
                name: 'Security',
                path: '/security',
                contents: (
                  <>
                    <Card>
                      <Card.Body>
                        <Card.Title>Security</Card.Title>
                        <FormCurrentUserPassword
                          onSubmit={updatePassword}
                          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                          username={emailAddress}
                        />
                      </Card.Body>
                    </Card>
                    <MultiFactorAuthentication
                      currentUser={currentUser}
                      organization={organization}
                      setupMfaEmail={setupMfaEmail}
                      enableMfaEmail={enableMfaEmail}
                      disableMfaEmail={disableMfaEmail}
                      mfaEmailLoading={mfaEmailLoading}
                      setupMfaPhone={setupMfaPhone}
                      enableMfaPhone={enableMfaPhone}
                      disableMfaPhone={disableMfaPhone}
                      mfaPhoneLoading={mfaPhoneLoading}
                      setupMfaTotp={setupMfaTotp}
                      enableMfaTotp={enableMfaTotp}
                      disableMfaTotp={disableMfaTotp}
                      mfaTotpLoading={mfaTotpLoading}
                      recoveryCode={recoveryCode}
                      rotateRecoveryCode={rotateRecoveryCode}
                      rotateRecoveryCodeLoading={rotateRecoveryCodeLoading}
                    />
                  </>
                ),
                dataTest: 'user-profile-view__tab--security',
              },
            ]}
          />
          <ModalWrapper isOpen={showReAuthModal} handleCloseModal={cancel}>
            <ConfirmationModal
              cancelAction={cancel}
              confirmAction={confirm}
              header='Disable 2-factor authentication'
              confirmActionText='Proceed'
            >
              <p>
                Your associated 2-factor authentication settings will be disabled until we can
                verify your new details.
              </p>
            </ConfirmationModal>
          </ModalWrapper>
        </Section>
      </Container>
    );
  }
};

export default DashboardUserProfileView;
